import React, { useState } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { Button, ConfirmDialog, ErrorResponse } from '~components';

import { CarePlan } from '~clients/models';

import { useActivateCarePlanMutation } from '~clients/actions/carePlans';

interface ComponentProps {
  plan: CarePlan;
  /** True once every activation checklist item passes. */
  canActivate: boolean;
}

// Surface the server's descriptive failures (400 body → data.failures[]), else a generic message.
const activationErrorMessage = (error: unknown): string => {
  const failures = (error as { data?: { data?: { failures?: unknown } } })?.data?.data?.failures;
  if (Array.isArray(failures) && failures.length > 0) return `Cannot activate: ${failures.join(', ')}`;
  return getErrorMessage(error);
};

/**
 * "Activate" action shown with the sidebar activation checklist (Draft plans only).
 * Enabled once the checklist is complete. Activation is a meaningful, one-way state change
 * (Draft → Active, no de-activation in the UI), so it goes through a confirmation dialog —
 * consistent with the delete confirmations elsewhere in this feature. On success the plan
 * flips to Active via the mutation's tag invalidation; on a 400 the server failures show
 * and the plan stays Draft.
 */
const ActivatePlanButton: React.FC<ComponentProps> = ({ plan, canActivate }) => {
  const { showToast } = useToast();
  const [activateCarePlan, { isLoading, error }] = useActivateCarePlanMutation();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const onConfirm = async (confirm: boolean) => {
    setIsConfirmOpen(false);
    if (!confirm) return;
    try {
      await activateCarePlan(plan.id).unwrap();
      showToast('success', 'Care plan activated');
    } catch {
      // Surfaced through the mutation error below; plan stays Draft.
    }
  };

  return (
    <div className="care-plan-activate">
      {error !== undefined && (
        <ErrorResponse dataCy="care-plan-activate-error" message={activationErrorMessage(error)} />
      )}
      {canActivate ? (
        <Button
          size="sm"
          color="success"
          disabled={isLoading}
          loading={isLoading}
          onClick={() => setIsConfirmOpen(true)}
          dataCy="care-plan-activate"
        >
          Activate
        </Button>
      ) : (
        <div className="care-plan-activate-hint text-muted small" data-cy="care-plan-activate-hint">
          Complete all items to activate
        </div>
      )}
      <ConfirmDialog
        isOpen={isConfirmOpen}
        useList={false}
        messages={
          <p>
            <strong>Activate this care plan?</strong> It will move from Draft to Active.
          </p>
        }
        onClose={onConfirm}
      />
    </div>
  );
};

export default ActivatePlanButton;
